import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  LogOut, LayoutDashboard, CalendarCheck, Clock, CheckCircle2, XCircle,
  Search, Trash2, Bike, Mail, Phone, ChevronDown, Wrench, Image, MessageSquareQuote, FileText, Users,
} from "lucide-react";
import { api } from "../lib/api";
import ServicesManager from "../components/ServicesManager";
import GalleryManager from "../components/GalleryManager";
import TestimonialsManager from "../components/TestimonialsManager";
import ContentManager from "../components/ContentManager";
import TeamManager from "../components/TeamManager";

const STATUS = {
  pending: { label: "Pendente", cls: "bg-amber-500/15 text-amber-400 border-amber-500/30" },
  confirmed: { label: "Confirmado", cls: "bg-cyan-500/15 text-cyan-300 border-cyan-500/30" },
  completed: { label: "Concluído", cls: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30" },
  cancelled: { label: "Cancelado", cls: "bg-red-500/15 text-red-400 border-red-500/30" },
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [stats, setStats] = useState({ total: 0, pending: 0, confirmed: 0, completed: 0 });
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("all");
  const [tab, setTab] = useState("bookings");
  const [loading, setLoading] = useState(true);

  const loadData = async () => {
    try {
      const [list, s] = await Promise.all([api.listBookings(), api.getStats()]);
      setBookings(list);
      setStats(s);
    } catch (err) {
      localStorage.removeItem("bf_admin_token");
      navigate("/admin");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!localStorage.getItem("bf_admin_token")) {
      navigate("/admin");
      return;
    }
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  const logout = () => {
    localStorage.removeItem("bf_admin_token");
    navigate("/admin");
  };

  const filtered = useMemo(() => {
    return bookings.filter((b) => {
      const matchQuery =
        !query ||
        [b.name, b.email, b.vehicleModel, b.serviceType, b.id]
          .join(" ").toLowerCase().includes(query.toLowerCase());
      const matchFilter = filter === "all" || b.status === filter;
      return matchQuery && matchFilter;
    });
  }, [bookings, query, filter]);

  const pendingCount = bookings.filter((b) => b.status === "pending").length;

  const updateStatus = async (id, status) => {
    setBookings((prev) => prev.map((b) => (b.id === id ? { ...b, status } : b)));
    try {
      await api.updateStatus(id, status);
      const s = await api.getStats();
      setStats(s);
    } catch (err) {
      loadData();
    }
  };

  const removeBooking = async (id) => {
    setBookings((prev) => prev.filter((b) => b.id !== id));
    try {
      await api.deleteBooking(id);
      const s = await api.getStats();
      setStats(s);
    } catch (err) {
      loadData();
    }
  };

  const statCards = [
    { key: "total", label: "Total de Marcações", value: stats.total, icon: CalendarCheck, color: "text-white", ring: "from-[#22d3ee]/20 to-[#22d3ee]/5 border-[#22d3ee]/30", ic: "text-[#22d3ee]" },
    { key: "pending", label: "Pendentes", value: stats.pending, icon: Clock, color: "text-amber-400", ring: "from-amber-500/20 to-amber-500/5 border-amber-500/30", ic: "text-amber-400" },
    { key: "confirmed", label: "Confirmadas", value: stats.confirmed, icon: CheckCircle2, color: "text-cyan-300", ring: "from-cyan-500/20 to-cyan-500/5 border-cyan-500/30", ic: "text-cyan-300" },
    { key: "completed", label: "Concluídas", value: stats.completed, icon: CheckCircle2, color: "text-emerald-400", ring: "from-emerald-500/20 to-emerald-500/5 border-emerald-500/30", ic: "text-emerald-400" },
  ];

  const filterTabs = [
    { key: "all", label: "Todas" },
    { key: "pending", label: "Pendentes" },
    { key: "confirmed", label: "Confirmadas" },
    { key: "completed", label: "Concluídas" },
    { key: "cancelled", label: "Canceladas" },
  ];

  return (
    <div className="min-h-screen bg-[#070b18]">
      {/* Top bar */}
      <header className="bf-glass border-b border-white/5 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#ff1e34] to-[#ff3d7f] flex items-center justify-center">
              <LayoutDashboard className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-white font-bold leading-tight">Admin Dashboard</h1>
              <p className="text-gray-500 text-xs">Barbo Factory - Gestão de Marcações</p>
            </div>
          </div>
          <button
            onClick={logout}
            className="flex items-center gap-2 text-sm text-gray-300 hover:text-white bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl px-4 py-2 transition-colors"
          >
            <LogOut className="w-4 h-4" /> Sair
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Section tabs */}
        <div className="flex flex-wrap gap-2 mb-8">
          {[
            { key: "bookings", label: "Marcações", icon: CalendarCheck },
            { key: "services", label: "Serviços", icon: Wrench },
            { key: "gallery", label: "Galeria", icon: Image },
            { key: "team", label: "Equipas", icon: Users },
            { key: "testimonials", label: "Testemunhos", icon: MessageSquareQuote },
            { key: "content", label: "Conteúdo", icon: FileText },
          ].map((tb) => (
            <button
              key={tb.key}
              onClick={() => setTab(tb.key)}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all ${
                tab === tb.key ? "bg-gradient-to-r from-[#67e8f9] to-[#22d3ee] text-[#0a1024]" : "bg-[#131b32] text-gray-300 hover:bg-[#1a2340] border border-white/5"
              }`}
            >
              <tb.icon className="w-4 h-4" /> {tb.label}
              {tb.key === "bookings" && pendingCount > 0 && (
                <span data-testid="pending-bookings-badge" className="ml-1 min-w-[20px] h-5 px-1.5 rounded-full bg-red-500 text-white text-[11px] font-bold flex items-center justify-center">
                  {pendingCount}
                </span>
              )}
            </button>
          ))}
        </div>

        {tab === "services" && <ServicesManager />}
        {tab === "gallery" && <GalleryManager />}
        {tab === "team" && <TeamManager />}
        {tab === "testimonials" && <TestimonialsManager />}
        {tab === "content" && <ContentManager />}

        {tab === "bookings" && (
        <div>
        {/* Stat cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {statCards.map((c) => (
            <div key={c.key} className="bf-glass rounded-2xl p-5 flex items-center gap-4">
              <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${c.ring} border flex items-center justify-center shrink-0`}>
                <c.icon className={`w-6 h-6 ${c.ic}`} />
              </div>
              <div>
                <div className={`text-2xl font-extrabold ${c.color}`}>{c.value}</div>
                <div className="text-gray-400 text-xs">{c.label}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Search + filters */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div className="relative w-full md:max-w-sm">
            <Search className="w-4 h-4 text-gray-500 absolute left-3.5 top-1/2 -translate-y-1/2" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Pesquisar por nome, email, veículo..."
              className="bf-input w-full rounded-xl pl-10 pr-4 py-2.5 text-sm placeholder:text-gray-500"
            />
          </div>
          <div className="flex flex-wrap gap-2">
            {filterTabs.map((f) => (
              <button
                key={f.key}
                onClick={() => setFilter(f.key)}
                className={`px-4 py-2 rounded-full text-xs font-semibold transition-all ${
                  filter === f.key
                    ? "bg-gradient-to-r from-[#67e8f9] to-[#22d3ee] text-[#0a1024]"
                    : "bg-[#131b32] text-gray-300 hover:bg-[#1a2340] border border-white/5"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {/* Bookings table (desktop) */}
        <div className="bf-glass rounded-2xl overflow-hidden hidden lg:block">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-400 border-b border-white/10">
                <th className="px-5 py-4 font-medium">ID</th>
                <th className="px-5 py-4 font-medium">Cliente</th>
                <th className="px-5 py-4 font-medium">Veículo</th>
                <th className="px-5 py-4 font-medium">Serviço</th>
                <th className="px-5 py-4 font-medium">Data</th>
                <th className="px-5 py-4 font-medium">Estado</th>
                <th className="px-5 py-4 font-medium text-right">Ações</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((b) => (
                <tr key={b.id} className="border-b border-white/5 hover:bg-white/[0.02] transition-colors">
                  <td className="px-5 py-4 text-gray-500 font-mono text-xs">{b.id}</td>
                  <td className="px-5 py-4">
                    <div className="text-white font-medium">{b.name}</div>
                    <div className="text-gray-500 text-xs">{b.email}</div>
                  </td>
                  <td className="px-5 py-4">
                    <div className="text-gray-300">{b.vehicleModel}</div>
                    <div className="text-gray-500 text-xs">{b.vehicleType}</div>
                  </td>
                  <td className="px-5 py-4 text-gray-300">{b.serviceType}</td>
                  <td className="px-5 py-4 text-gray-300">{b.date}</td>
                  <td className="px-5 py-4">
                    <div className="relative inline-block">
                      <select
                        value={b.status}
                        onChange={(e) => updateStatus(b.id, e.target.value)}
                        className={`appearance-none pr-7 pl-3 py-1.5 rounded-full text-xs font-semibold border cursor-pointer bg-transparent ${STATUS[b.status].cls}`}
                      >
                        {Object.keys(STATUS).map((s) => (
                          <option key={s} value={s} className="bg-[#131b32] text-white">{STATUS[s].label}</option>
                        ))}
                      </select>
                      <ChevronDown className="w-3.5 h-3.5 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none opacity-70" />
                    </div>
                  </td>
                  <td className="px-5 py-4 text-right">
                    <button onClick={() => removeBooking(b.id)} className="text-gray-500 hover:text-red-400 transition-colors">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-5 py-12 text-center text-gray-500">Nenhuma marcação encontrada.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Bookings cards (mobile) */}
        <div className="lg:hidden space-y-4">
          {filtered.map((b) => (
            <div key={b.id} className="bf-glass rounded-2xl p-5">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div className="text-white font-semibold">{b.name}</div>
                  <div className="text-gray-500 text-xs font-mono">{b.id}</div>
                </div>
                <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${STATUS[b.status].cls}`}>{STATUS[b.status].label}</span>
              </div>
              <div className="space-y-1.5 text-sm text-gray-400">
                <div className="flex items-center gap-2"><Bike className="w-4 h-4 text-[#22d3ee]" /> {b.vehicleModel} · {b.vehicleType}</div>
                <div className="flex items-center gap-2"><CalendarCheck className="w-4 h-4 text-[#22d3ee]" /> {b.serviceType} · {b.date}</div>
                <div className="flex items-center gap-2"><Mail className="w-4 h-4 text-[#22d3ee]" /> {b.email}</div>
                <div className="flex items-center gap-2"><Phone className="w-4 h-4 text-[#22d3ee]" /> {b.phone}</div>
              </div>
              <div className="flex items-center gap-2 mt-4">
                <select
                  value={b.status}
                  onChange={(e) => updateStatus(b.id, e.target.value)}
                  className="bf-input flex-1 rounded-lg px-3 py-2 text-xs"
                >
                  {Object.keys(STATUS).map((s) => (
                    <option key={s} value={s} className="bg-[#131b32]">{STATUS[s].label}</option>
                  ))}
                </select>
                <button onClick={() => removeBooking(b.id)} className="p-2 rounded-lg bg-red-500/10 text-red-400 border border-red-500/20">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
          {filtered.length === 0 && (
            <div className="bf-glass rounded-2xl p-12 text-center text-gray-500">Nenhuma marcação encontrada.</div>
          )}
        </div>
        </div>
        )}
      </main>
    </div>
  );
};

export default AdminDashboard;
